/** A moment marker on the seek bar, as the Stream API returns it. */
export interface MomentLike {
  timestamp?: unknown;
  label?: unknown;
}

/** Width of the band, in CSS pixels, around a marker in which a scrub still counts as "on" it. */
export const MOMENT_SCRUB_TOLERANCE_PX = 20;

/** The text a moment shows on the seek bar, or undefined when it has none. */
export function getMomentLabel(moment: MomentLike): string | undefined {
  return (moment?.label ? String(moment.label).trim() : '') || undefined;
}

/**
 * The moment closest to `time`, if it lies within `toleranceSeconds` of it.
 *
 * Touch has no hover, so the marker's own hover label never shows. While scrubbing, the
 * finger passes near a dot rather than exactly over it, hence the tolerance. Ties go to
 * the earlier moment. Moments without a usable timestamp or without a label are skipped,
 * so an unlabelled one never hides a labelled one beside it.
 *
 * `bounds` clamps each timestamp into the seekable range, as the bar does when it draws
 * the dot: a moment past the end sits at the end, and is found there.
 */
export function findMomentNear<T extends MomentLike>(
  moments: readonly T[],
  time: number,
  toleranceSeconds: number,
  bounds?: { start: number; end: number },
): T | undefined {
  if (!Number.isFinite(time) || !Number.isFinite(toleranceSeconds)) {
    return undefined;
  }

  let best: T | undefined;
  let bestDistance = Infinity;
  let bestTimestamp = Infinity;
  for (const moment of moments) {
    if (!getMomentLabel(moment)) continue;
    let timestamp = Number(moment?.timestamp);
    if (!Number.isFinite(timestamp)) continue;
    if (bounds) {
      timestamp = Math.max(bounds.start, Math.min(bounds.end, timestamp));
    }
    const distance = Math.abs(timestamp - time);
    if (distance > toleranceSeconds) continue;
    if (
      distance < bestDistance ||
      (distance === bestDistance && timestamp < bestTimestamp)
    ) {
      best = moment;
      bestDistance = distance;
      bestTimestamp = timestamp;
    }
  }
  return best;
}

/** Converts a pixel tolerance on a bar `barWidthPx` wide covering `spanSeconds` to seconds. */
export function toleranceSecondsForBar(
  spanSeconds: number,
  barWidthPx: number,
  tolerancePx: number = MOMENT_SCRUB_TOLERANCE_PX,
): number {
  if (!(spanSeconds > 0) || !(barWidthPx > 0)) return 0;
  return (tolerancePx / barWidthPx) * spanSeconds;
}
